// Analítica — Postventa | Ventas sub-tabs (P1, Roger 2026-10-24 17:03 CST, NO DEPLOY).
//
// Jocelin (Oct-23 call) reads her retention numbers per GM band of "meses sin
// visita" (5–11 / 12–18 vencidos / 13–48 PRINCIPAL / 13–96): en planta,
// contactados, contestaron, citas, % conversión, WA enviados / respondidos; by
// day / week, asesor and sucursal. Ventas (Roger): CRM leads per day / fuente /
// asesor -> contactados -> alcanzados (humano) -> citas / prueba de manejo.
//
// Data: GET /api/analytics/postventa and GET /api/analytics/ventas (server-side
// aggregation, cached 5 min, see web_api_server "P1 ANALÍTICA SPLIT"). Every
// payload carries `definitions` (the exact rule per metric) which this page
// renders under "¿Cómo se calcula?" so Jocelin can validate them in the meeting.
// Shared filters: rango de fechas · sucursal · asesor (fuente on Ventas).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '../../services/api';

export const SUB_TABS = [
  { key: 'postventa', label: 'Postventa' },
  { key: 'ventas', label: 'Ventas' },
];

// Branches of Grupo Cruces (canonical labels the backend emits). Free text is
// also accepted by the API; these are just the quick picks.
export const SUCURSAL_OPTIONS = ['Villahermosa', 'Cárdenas', 'Comalcalco'];

const MX_OFFSET_MS = -6 * 60 * 60 * 1000;

// YYYY-MM-DD "today" in America/Mexico_City (fixed UTC-6, no DST since 2022).
export function mxToday(now = new Date()) {
  return new Date(now.getTime() + MX_OFFSET_MS).toISOString().slice(0, 10);
}

// Default = last 30 days ending today (MX), mirrors the API default.
export function defaultRange(now = new Date()) {
  const end = mxToday(now);
  const d = new Date(`${end}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 29);
  return { start: d.toISOString().slice(0, 10), end };
}

export const fmtPct = (value) => (value === null || value === undefined || Number.isNaN(Number(value)) ? '—' : `${Number(value).toFixed(1)} %`);
export const fmtNum = (value) => (Number.isFinite(Number(value)) ? Number(value).toLocaleString('es-MX') : '0');

// Row with an "estimado" share renders "19 (14 est.)" so the gap is visible.
export function fmtWithEstimate(value, estimated) {
  const base = fmtNum(value);
  return Number(estimated) > 0 ? `${base} (${fmtNum(estimated)} est.)` : base;
}

const thCls = 'px-2 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap';
const tdCls = 'px-2 py-1.5 text-sm whitespace-nowrap';

function Th({ children, align = 'left', title }) {
  return <th title={title} className={`${thCls} ${align === 'right' ? 'text-right' : ''}`} style={{ color: 'var(--muted)' }}>{children}</th>;
}
function Td({ children, align = 'right', strong }) {
  return <td className={`${tdCls} ${align === 'right' ? 'text-right tabular-nums' : ''} ${strong ? 'font-semibold' : ''} text-foreground`}>{children}</td>;
}

const slug = (text) => String(text).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

function Kpi({ label, value, hint }) {
  return (
    <div className="card" data-testid={`kpi-${slug(label)}`}>
      <dt className="text-xs sm:text-sm font-medium leading-tight" style={{ color: 'var(--muted)' }}>{label}</dt>
      <dd className="text-lg sm:text-xl font-semibold text-foreground" data-testid={`kpi-value-${slug(label)}`}>{value}</dd>
      {hint && <p className="text-[11px] mt-0.5" style={{ color: 'var(--muted)' }}>{hint}</p>}
    </div>
  );
}

export function Definitions({ definitions, sources }) {
  const [open, setOpen] = useState(false);
  const entries = Object.entries(definitions || {});
  if (!entries.length) return null;
  return (
    <div className="card">
      <button type="button" onClick={() => setOpen((v) => !v)} className="text-sm font-medium text-foreground" data-testid="definitions-toggle">
        {open ? '▾' : '▸'} ¿Cómo se calcula cada métrica?
      </button>
      {open && (
        <dl className="mt-3 space-y-2" data-testid="definitions-list">
          {entries.map(([key, text]) => (
            <div key={key}>
              <dt className="text-xs font-semibold text-foreground">{key}</dt>
              <dd className="text-xs" style={{ color: 'var(--muted)' }}>{text}</dd>
            </div>
          ))}
          {sources && (
            <div>
              <dt className="text-xs font-semibold text-foreground">fuentes de datos</dt>
              {Object.entries(sources).map(([k, v]) => (
                <dd key={k} className="text-xs" style={{ color: 'var(--muted)' }}><span className="font-medium">{k}:</span> {v}</dd>
              ))}
            </div>
          )}
        </dl>
      )}
    </div>
  );
}

// Shared filter bar. `extra` lets Ventas add the fuente input.
export function SharedFilters({ filters, onChange, asesorOptions = [], showSucursal = true, extra = null }) {
  const set = (patch) => onChange({ ...filters, ...patch });
  return (
    <div className="flex flex-wrap items-end gap-3" data-testid="shared-filters">
      <label className="text-xs" style={{ color: 'var(--muted)' }}>
        Desde
        <input type="date" aria-label="Desde" className="input-field w-auto block" value={filters.start} max={filters.end} onChange={(e) => set({ start: e.target.value })} />
      </label>
      <label className="text-xs" style={{ color: 'var(--muted)' }}>
        Hasta
        <input type="date" aria-label="Hasta" className="input-field w-auto block" value={filters.end} min={filters.start} onChange={(e) => set({ end: e.target.value })} />
      </label>
      {showSucursal && (
        <label className="text-xs" style={{ color: 'var(--muted)' }}>
          Sucursal
          <select aria-label="Sucursal" className="input-field w-auto block" value={filters.sucursal || ''} onChange={(e) => set({ sucursal: e.target.value })}>
            <option value="">Todas</option>
            {SUCURSAL_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      )}
      <label className="text-xs" style={{ color: 'var(--muted)' }}>
        Asesor
        <input list="analytics-asesor-options" aria-label="Asesor" className="input-field w-auto block" placeholder="Todos" value={filters.asesor || ''} onChange={(e) => set({ asesor: e.target.value })} />
        <datalist id="analytics-asesor-options">
          {asesorOptions.map((n) => <option key={n} value={n} />)}
        </datalist>
      </label>
      {extra}
    </div>
  );
}

// Debounced fetch hook shared by both panels. Aborts stale requests so a quick
// filter change never lands an older payload on top of a newer one.
function useAnalytics(path, params) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cacheState, setCacheState] = useState('');
  const abortRef = useRef(null);
  const key = JSON.stringify(params);

  const load = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(path, { params: JSON.parse(key), signal: controller.signal });
      if (controller.signal.aborted) return;
      setData(res.data || null);
      setCacheState(res.headers?.['x-analytics-cache'] || '');
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.name === 'AbortError' || controller.signal.aborted) return;
      setError(e);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [path, key]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return { data, loading, error, reload: load, cacheState };
}

function Status({ loading, error, reload, data, cacheState }) {
  if (error) {
    return (
      <div className="flex items-center gap-3 text-sm" role="alert">
        <span style={{ color: 'var(--muted)' }}>No se pudo cargar la analítica.</span>
        <button type="button" onClick={reload} className="btn-secondary text-xs">Reintentar</button>
      </div>
    );
  }
  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-32" data-testid="analytics-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }
  if (!data) return null;
  return (
    <p className="text-[11px]" style={{ color: 'var(--muted)' }}>
      {data.period?.start} → {data.period?.end} · días America/Mexico_City
      {data.cartera?.as_of ? ` · cartera actualizada ${new Date(Number(data.cartera.as_of)).toLocaleString('es-MX')}` : ''}
      {loading ? ' · actualizando…' : ''}
      {cacheState ? ` · ${cacheState === 'HIT' ? 'desde caché' : 'calculado ahora'}` : ''}
    </p>
  );
}

// ---------------------------------------------------------------------------
// POSTVENTA
// ---------------------------------------------------------------------------
export function PostventaBandTable({ bands, sinBanda, totals }) {
  const rows = [...(bands || []), ...(sinBanda ? [sinBanda] : [])];
  return (
    <div className="card overflow-x-auto">
      <h3 className="text-base font-medium text-foreground mb-2">Bandas de meses sin visita (PEN)</h3>
      <table className="min-w-full" data-testid="band-table">
        <thead>
          <tr>
            <Th>Banda</Th>
            <Th align="right" title="Unidades de la cartera en la banda, sin fuera de planta ni pruebas">En planta</Th>
            <Th align="right" title="Intentos de llamada en el rango">Llamadas</Th>
            <Th align="right" title="Unidades distintas con ≥1 llamada">Contactados</Th>
            <Th align="right" title="Unidades con ≥1 llamada contestada">Contestaron</Th>
            <Th align="right" title="Citas agendadas por el agente">Citas</Th>
            <Th align="right" title="citas ÷ contactados">% conversión</Th>
            <Th align="right">WA enviados</Th>
            <Th align="right">WA respondidos</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((b) => (
            <tr key={b.key} data-testid={`band-row-${b.key}`} className={b.main ? 'bg-primary/10' : ''}>
              <Td align="left" strong={b.main}>{b.label}{b.main ? ' ★' : ''}</Td>
              <Td>{fmtNum(b.en_planta)}</Td>
              <Td>{fmtNum(b.llamadas)}</Td>
              <Td>{fmtNum(b.contactados)}</Td>
              <Td>{fmtWithEstimate(b.contestaron, b.contestaron_estimados)}</Td>
              <Td>{fmtNum(b.citas)}</Td>
              <Td strong={b.main}>{fmtPct(b.conversion_pct)}</Td>
              <Td>{fmtNum(b.wa_enviados)}</Td>
              <Td>{fmtNum(b.wa_respondidos)}</Td>
            </tr>
          ))}
          {totals && (
            <tr data-testid="band-row-total" className="border-t border-border">
              <Td align="left" strong>Total</Td>
              <Td>—</Td>
              <Td strong>{fmtNum(totals.llamadas)}</Td>
              <Td strong>{fmtNum(totals.contactados)}</Td>
              <Td strong>{fmtWithEstimate(totals.contestaron, totals.contestaron_estimados)}</Td>
              <Td strong>{fmtNum(totals.citas)}</Td>
              <Td strong>{fmtPct(totals.conversion_pct)}</Td>
              <Td strong>{fmtNum(totals.wa_enviados)}</Td>
              <Td strong>{fmtNum(totals.wa_respondidos)}</Td>
            </tr>
          )}
        </tbody>
      </table>
      <p className="text-[11px] mt-2" style={{ color: 'var(--muted)' }}>
        ★ banda principal. Las bandas se traslapan (una unidad puede estar en 13–48 y 13–96). "est." = contestadas
        estimadas por duración ≥ 5 s en llamadas anteriores al 29-sep (sin verificación de contestadora).
      </p>
    </div>
  );
}

export function PostventaBreakdownTable({ title, rows, testId }) {
  return (
    <div className="card overflow-x-auto">
      <h3 className="text-base font-medium text-foreground mb-2">{title}</h3>
      {rows && rows.length ? (
        <table className="min-w-full" data-testid={testId}>
          <thead>
            <tr>
              <Th>{title.includes('asesor') ? 'Asesor' : 'Sucursal'}</Th>
              <Th align="right">En planta</Th>
              <Th align="right">Llamadas</Th>
              <Th align="right">Contactados</Th>
              <Th align="right">Contestaron</Th>
              <Th align="right">Citas</Th>
              <Th align="right">% conv.</Th>
              <Th align="right">WA env.</Th>
              <Th align="right">WA resp.</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <Td align="left">{r.label}</Td>
                <Td>{fmtNum(r.en_planta)}</Td>
                <Td>{fmtNum(r.llamadas)}</Td>
                <Td>{fmtNum(r.contactados)}</Td>
                <Td>{fmtWithEstimate(r.contestaron, r.contestaron_estimados)}</Td>
                <Td>{fmtNum(r.citas)}</Td>
                <Td>{fmtPct(r.conversion_pct)}</Td>
                <Td>{fmtNum(r.wa_enviados)}</Td>
                <Td>{fmtNum(r.wa_respondidos)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-sm" style={{ color: 'var(--muted)' }}>Sin datos en el rango.</p>
      )}
    </div>
  );
}

function DailySeries({ title, data, bars, lines, xKey = 'date' }) {
  return (
    <div className="card min-w-0">
      <h3 className="text-base font-medium text-foreground mb-2">{title}</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey={xKey} tick={{ fontSize: 10 }} tickFormatter={(v) => String(v).slice(5)} />
            <YAxis tick={{ fontSize: 11 }} width={36} allowDecimals={false} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {bars.map((b) => <Bar key={b.key} dataKey={b.key} name={b.name} fill={b.color} radius={[3, 3, 0, 0]} isAnimationActive={false} />)}
            {(lines || []).map((l) => <Line key={l.key} type="monotone" dataKey={l.key} name={l.name} stroke={l.color} strokeWidth={2} dot={false} isAnimationActive={false} />)}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function PostventaPanel({ filters, onFiltersChange }) {
  const params = useMemo(() => ({
    start: filters.start, end: filters.end,
    ...(filters.sucursal ? { sucursal: filters.sucursal } : {}),
    ...(filters.asesor ? { asesor: filters.asesor } : {}),
  }), [filters.start, filters.end, filters.sucursal, filters.asesor]);
  const { data, loading, error, reload, cacheState } = useAnalytics('/analytics/postventa', params);
  const asesorOptions = useMemo(() => (data?.by_asesor || []).map((r) => r.label).filter((l) => l && l !== 'Sin asesor'), [data]);
  const [granularity, setGranularity] = useState('daily');
  const totals = data?.totals || {};
  const main = (data?.bands || []).find((b) => b.main) || {};
  return (
    <div className="space-y-4" data-testid="postventa-panel">
      <SharedFilters filters={filters} onChange={onFiltersChange} asesorOptions={asesorOptions} />
      <Status loading={loading} error={error} reload={reload} data={data} cacheState={cacheState} />
      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-6">
            <Kpi label="En planta (cartera)" value={fmtNum(data.cartera?.en_planta)} hint={`${fmtNum(data.cartera?.fuera_de_planta)} fuera de planta · ${fmtNum(data.cartera?.sin_banda)} sin banda`} />
            <Kpi label="Llamadas" value={fmtNum(totals.llamadas)} hint={`${fmtNum(totals.contactados)} unidades contactadas`} />
            <Kpi label="Contestaron" value={fmtWithEstimate(totals.contestaron, totals.contestaron_estimados)} hint={fmtPct(totals.contestaron_pct)} />
            <Kpi label="Citas agendadas" value={fmtNum(totals.citas)} hint={`conversión ${fmtPct(totals.conversion_pct)} · B.PRO ${fmtNum(totals.citas_bpro)}`} />
            <Kpi label="WhatsApp" value={`${fmtNum(totals.wa_enviados)} / ${fmtNum(totals.wa_respondidos)}`} hint="enviados / respondidos" />
            <Kpi label="13–48 (principal)" value={fmtPct(main.conversion_pct)} hint={`${fmtNum(main.citas)} citas · ${fmtNum(main.contactados)} contactados · ${fmtNum(main.en_planta)} en planta`} />
          </div>
          <PostventaBandTable bands={data.bands} sinBanda={data.sin_banda} totals={data.totals} />
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: 'var(--muted)' }}>Serie:</span>
            {['daily', 'weekly'].map((g) => (
              <button key={g} type="button" onClick={() => setGranularity(g)} className={`px-3 py-1 text-xs rounded-full ${granularity === g ? 'bg-primary text-white' : 'bg-card text-foreground border border-border'}`}>
                {g === 'daily' ? 'Por día' : 'Por semana'}
              </button>
            ))}
          </div>
          <DailySeries
            title={granularity === 'daily' ? 'Actividad por día' : 'Actividad por semana'}
            data={granularity === 'daily' ? data.daily : data.weekly}
            xKey={granularity === 'daily' ? 'date' : 'week'}
            bars={[
              { key: 'llamadas', name: 'Llamadas', color: '#3b82f6' },
              { key: 'contestaron', name: 'Contestaron', color: '#22c55e' },
              { key: 'citas', name: 'Citas', color: '#f59e0b' },
            ]}
            lines={[
              { key: 'wa_enviados', name: 'WA enviados', color: '#8b5cf6' },
              { key: 'wa_respondidos', name: 'WA respondidos', color: '#ec4899' },
            ]}
          />
          <PostventaBreakdownTable title="Por asesor (ejecutivo)" rows={data.by_asesor} testId="asesor-table" />
          <PostventaBreakdownTable title="Por sucursal" rows={data.by_sucursal} testId="sucursal-table" />
          <p className="text-[11px]" style={{ color: 'var(--muted)' }} data-testid="coverage-note">
            Cobertura: {fmtPct(data.coverage?.calls_with_vin_pct)} de las llamadas traen VIN · {fmtPct(data.coverage?.calls_with_asesor_pct)} traen asesor ·
            {' '}{fmtPct(data.coverage?.citas_with_vin_pct)} de las citas traen VIN · {fmtPct(data.coverage?.wa_joined_pct)} de los WhatsApp se unen a una unidad
            {Number(data.coverage?.wa_ambiguous) > 0 ? ` (${fmtNum(data.coverage.wa_ambiguous)} ambiguos)` : ''}.
            Lo que no se puede atribuir cae en "Sin banda" / "Sin asesor" / "Sin sucursal" — nunca se adivina.
          </p>
          <Definitions definitions={data.definitions} sources={data.sources} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// VENTAS
// ---------------------------------------------------------------------------
export function VentasBreakdownTable({ title, rows, firstCol, testId }) {
  return (
    <div className="card overflow-x-auto">
      <h3 className="text-base font-medium text-foreground mb-2">{title}</h3>
      {rows && rows.length ? (
        <table className="min-w-full" data-testid={testId}>
          <thead>
            <tr>
              <Th>{firstCol}</Th>
              <Th align="right">Leads</Th>
              <Th align="right">Llamadas</Th>
              <Th align="right">Contactados</Th>
              <Th align="right">Alcanzados</Th>
              <Th align="right">Citas</Th>
              <Th align="right">Prueba manejo</Th>
              <Th align="right">No contestó</Th>
              <Th align="right">Ocupado</Th>
              <Th align="right">Fallido</Th>
              <Th align="right">% conv.</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <Td align="left">{r.label}</Td>
                <Td>{fmtNum(r.leads)}</Td>
                <Td>{fmtNum(r.llamadas)}</Td>
                <Td>{fmtNum(r.contactados)}</Td>
                <Td>{fmtWithEstimate(r.alcanzados, r.alcanzados_estimados)}</Td>
                <Td>{fmtWithEstimate(r.citas, r.citas_estimadas)}</Td>
                <Td>{fmtNum(r.pruebas_manejo)}</Td>
                <Td>{fmtNum(r.no_contesto)}</Td>
                <Td>{fmtNum(r.ocupado)}</Td>
                <Td>{fmtNum(r.fallido)}</Td>
                <Td>{fmtPct(r.conversion_pct)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-sm" style={{ color: 'var(--muted)' }}>Sin datos en el rango.</p>
      )}
    </div>
  );
}

export function VentasPanel({ filters, onFiltersChange }) {
  const params = useMemo(() => ({
    start: filters.start, end: filters.end,
    ...(filters.asesor ? { asesor: filters.asesor } : {}),
    ...(filters.fuente ? { fuente: filters.fuente } : {}),
  }), [filters.start, filters.end, filters.asesor, filters.fuente]);
  const { data, loading, error, reload, cacheState } = useAnalytics('/analytics/ventas', params);
  const asesorOptions = useMemo(() => (data?.by_asesor || []).map((r) => r.label).filter((l) => l && l !== 'Sin asesor'), [data]);
  const fuenteOptions = useMemo(() => (data?.by_fuente || []).map((r) => r.label).filter(Boolean), [data]);
  const totals = data?.totals || {};
  const fuenteInput = (
    <label className="text-xs" style={{ color: 'var(--muted)' }}>
      Fuente
      <input list="analytics-fuente-options" aria-label="Fuente" className="input-field w-auto block" placeholder="Todas" value={filters.fuente || ''} onChange={(e) => onFiltersChange({ ...filters, fuente: e.target.value })} />
      <datalist id="analytics-fuente-options">{fuenteOptions.map((n) => <option key={n} value={n} />)}</datalist>
    </label>
  );
  return (
    <div className="space-y-4" data-testid="ventas-panel">
      <SharedFilters filters={filters} onChange={onFiltersChange} asesorOptions={asesorOptions} showSucursal={false} extra={fuenteInput} />
      <Status loading={loading} error={error} reload={reload} data={data} cacheState={cacheState} />
      {data && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-6">
            <Kpi label="Leads recibidos" value={fmtNum(totals.leads)} hint={`${fmtNum(data.campaigns)} campañas`} />
            <Kpi label="Contactados" value={fmtNum(totals.contactados)} hint={`${fmtPct(totals.contacto_pct)} de los leads · ${fmtNum(totals.llamadas)} llamadas`} />
            <Kpi label="Alcanzados (humano)" value={fmtWithEstimate(totals.alcanzados, totals.alcanzados_estimados)} hint={fmtPct(totals.alcance_pct)} />
            <Kpi label="Citas" value={fmtWithEstimate(totals.citas, totals.citas_estimadas)} hint={`conversión ${fmtPct(totals.conversion_pct)}`} />
            <Kpi label="Pruebas de manejo" value={fmtNum(totals.pruebas_manejo)} />
            <Kpi label="No contestó / ocupado / fallido" value={`${fmtNum(totals.no_contesto)} / ${fmtNum(totals.ocupado)} / ${fmtNum(totals.fallido)}`} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="card min-w-0">
              <h3 className="text-base font-medium text-foreground mb-2">Embudo</h3>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.funnel || []} layout="vertical" margin={{ left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={120} />
                    <Tooltip />
                    <Bar dataKey="value" name="Leads" fill="#2563eb" radius={[0, 6, 6, 0]} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="lg:col-span-2 min-w-0">
              <DailySeries
                title="Embudo por día"
                data={data.daily}
                bars={[
                  { key: 'leads', name: 'Leads', color: '#94a3b8' },
                  { key: 'contactados', name: 'Contactados', color: '#3b82f6' },
                  { key: 'alcanzados', name: 'Alcanzados', color: '#22c55e' },
                  { key: 'citas', name: 'Citas', color: '#f59e0b' },
                ]}
                lines={[{ key: 'no_contesto', name: 'No contestó', color: '#ef4444' }]}
              />
            </div>
          </div>
          <VentasBreakdownTable title="Por fuente" rows={data.by_fuente} firstCol="Fuente" testId="ventas-fuente-table" />
          <VentasBreakdownTable title="Por asesor" rows={data.by_asesor} firstCol="Asesor" testId="ventas-asesor-table" />
          <Definitions definitions={data.definitions} sources={data.sources} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tab shell
// ---------------------------------------------------------------------------
export default function AnalyticsSplit({ tab, onTabChange, legacy = null, legacyLabel = 'Campañas' }) {
  const [filters, setFilters] = useState(() => ({ ...defaultRange(), sucursal: '', asesor: '', fuente: '' }));
  const tabs = legacy ? [...SUB_TABS, { key: 'campanas', label: legacyLabel }] : SUB_TABS;
  return (
    <div className="space-y-4" data-testid="analytics-split">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl font-bold leading-7 text-foreground sm:text-2xl truncate">Analítica</h2>
          <p className="mt-1 text-sm" style={{ color: 'var(--muted)' }}>Postventa (bandas PEN) y Ventas (leads CRM), separados</p>
        </div>
        <div className="inline-flex rounded-full shadow-sm" role="tablist" aria-label="Analítica">
          {tabs.map((t, i) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => onTabChange(t.key)}
              className={`px-4 py-2 text-sm font-medium ${i === 0 ? 'rounded-l-full' : ''} ${i === tabs.length - 1 ? 'rounded-r-full' : ''} ${tab === t.key ? 'bg-primary text-white' : 'bg-card text-foreground border border-border hover:bg-background'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {tab === 'postventa' && <PostventaPanel filters={filters} onFiltersChange={setFilters} />}
      {tab === 'ventas' && <VentasPanel filters={filters} onFiltersChange={setFilters} />}
      {tab === 'campanas' && legacy}
    </div>
  );
}
